import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { qtyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { RelatedDocLink } from "@/modules/docs/RelatedDocLink";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import { FieldGroup } from "@/modules/shell/FieldGroup";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { DocActivity } from "@/modules/shell/DocActivity";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { RemindMeButton } from "@/modules/shell/RemindMeButton";
import { useRecordRecentDoc } from "@/modules/shell/use-record-recent-doc";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { getConversion } from "@/shared/api/conversions";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";

export function ConversionDetailPage() {
  const { companySlug, conversionId } = useParams();

  const detailQuery = useQuery({
    queryKey: queryKeys.conversions.detail(
      companySlug ?? "",
      conversionId ?? "",
    ),
    enabled: Boolean(companySlug && conversionId),
    queryFn: () => getConversion(companySlug!, conversionId!),
  });
  const errorMsg = queryErrorMessage(detailQuery.error, detailQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  const cv = detailQuery.data?.conversion;

  useRecordRecentDoc({
    companySlug,
    kind: "stockConversion",
    id: cv?.id,
    label: cv?.number,
    path:
      companySlug && cv?.id
        ? `/c/${companySlug}/inventory/conversions/${cv.id}`
        : undefined,
    hint: cv ? `${cv.fromName} → ${cv.toName}` : undefined,
  });

  if (!companySlug || !conversionId) return null;

  const displayError =
    errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;

  if (detailQuery.isPending || !cv) {
    return (
      <PageFrame width="narrow" className="py-4">
        {displayError ? (
          <Alert variant="destructive">{displayError}</Alert>
        ) : (
          <Alert variant="muted">Loading conversion…</Alert>
        )}
      </PageFrame>
    );
  }

  const whLabel = [cv.warehouseName, cv.warehouseCode]
    .filter(Boolean)
    .join(" · ");

  return (
    <PageFrame width="narrow">
      <div className="mb-3">
        <DeskDocBackLink
          listBasePath={`/c/${companySlug}/inventory/convert`}
          listLabel="Convert"
        />
      </div>
      <PageHeader
        eyebrow="Conversion"
        title={cv.number}
        description={[whLabel, "stock unpack / pack"]
          .filter(Boolean)
          .join(" · ")}
        actions={
          <RemindMeButton
            companySlug={companySlug}
            kind="stock_conversion"
            docId={conversionId}
            docNumber={cv.number}
            partyName={`${cv.fromName} → ${cv.toName}`}
          />
        }
      />
      <div className="mt-2">
        <LifecycleStatus value={cv.lifecycle} showLabel />
      </div>
      <TagsAssigneesPanel
        companySlug={companySlug}
        docType="stock_conversion"
        docId={conversionId}
        linkPath={`/c/${companySlug}/inventory/conversions/${conversionId}`}
        docLabel={[cv.number, `${cv.fromName} → ${cv.toName}`]
          .filter(Boolean)
          .join(" · ")}
      />

      <FieldGroup title="Summary" className="mt-6" level="sub">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-[var(--color-muted)]">From</dt>
          <dd className="m-0">
            <RelatedDocLink
              companySlug={companySlug}
              kind="product"
              docId={cv.fromProductId}
            >
              {cv.fromName}
            </RelatedDocLink>
            <span className="ml-2 font-mono tabular-nums text-xs text-[var(--color-muted)]">
              {qtyDisplay(cv.qtyFrom)} @ {cv.unitCostFrom}
            </span>
          </dd>
          <dt className="text-[var(--color-muted)]">To</dt>
          <dd className="m-0">
            <RelatedDocLink
              companySlug={companySlug}
              kind="product"
              docId={cv.toProductId}
            >
              {cv.toName}
            </RelatedDocLink>
            <span className="ml-2 font-mono tabular-nums text-xs text-[var(--color-muted)]">
              {qtyDisplay(cv.qtyTo)} @ {cv.unitCostTo}
            </span>
          </dd>
          {whLabel ? (
            <>
              <dt className="text-[var(--color-muted)]">Warehouse</dt>
              <dd className="m-0">{whLabel}</dd>
            </>
          ) : null}
          {cv.postedAt ? (
            <>
              <dt className="text-[var(--color-muted)]">Posted</dt>
              <dd className="m-0 text-xs">{cv.postedAt}</dd>
            </>
          ) : null}
          {cv.notes ? (
            <>
              <dt className="text-[var(--color-muted)]">Notes</dt>
              <dd className="m-0">{cv.notes}</dd>
            </>
          ) : null}
        </dl>
      </FieldGroup>

      {displayError ? (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      ) : null}

      <DocActivity
        companySlug={companySlug}
        docType="stock_conversion"
        docId={conversionId}
        linkPath={`/c/${companySlug}/inventory/conversions/${conversionId}`}
      />
    </PageFrame>
  );
}
